import React from "react";
import styled from "styled-components";

import * as Animation from "~/Animation";
import { Color, almostWhite, veryWhiteGrey } from "~/Color";
import { Component as ErrorMessageComponent } from "~/ErrorMessage";
import { Binoculars } from "~/Icon";
import * as Layout from "~/Layout";
import { Component as LoadingComponent } from "~/Loading";
import * as Shadow from "~/Shadow";
import * as Typography from "~/Typography";

type Props = { readonly fixed?: boolean };

export const Component: React.Fn<Props> = ({ children, ...props }) => (
  <Scroll.Component>
    <Table.Component {...props}>{children}</Table.Component>
  </Scroll.Component>
);

namespace Table {
  export const Component = styled.table<Props>`
    box-sizing: border-box;
    table-layout: ${({ fixed }) => (fixed ? "fixed" : "auto")};
    border-collapse: collapse;
    width: 100%;

    & th,
    & td {
      padding: ${() => Layout.Block.padding()};
      white-space: nowrap;

      &:first-of-type {
        border-left: none;
      }

      &:last-of-type {
        border-right: none;
      }
    }
  `;
}

export namespace Scroll {
  export const Component = styled.div`
    overflow-x: auto;
  `;
}

export namespace Caption {
  type Props = {
    readonly left?: boolean;
    readonly right?: boolean;
  };

  export const Component = styled.caption<Props>`
    width: 100%;
    caption-side: top;
    padding: ${Layout.Inline.padding()};
    padding-bottom: 1.1em;
    padding-left: 0;

    font-size: 1em;
    font-weight: 500;
    white-space: nowrap;
    line-height: 0.6em;
    color: inherit;

    ${({ left }) => (left ? "text-align: left" : "")}
    ${({ right }) => (right ? "text-align: right" : "")}
  `;
}

export namespace Head {
  export const Component = styled.thead`
    & > tr {
      background: ${() => Background.color()};
      &:hover {
        box-shadow: none;
        background: ${() => Background.color()};
      }
    }
  `;
}

export namespace Header {
  export const Component = styled.th`
    /* TODO: Resolve conflict with legacy CSS */
    white-space: normal;
    line-height: 20px;
    border-top: 1px solid ${() => veryWhiteGrey()};
  `;
}

export namespace Body {
  export const Component: Component = ({ children }) => (
    <tbody>{children}</tbody>
  );
}

export namespace Footer {
  export const Component: Component = ({ children }) => (
    <tfoot>{children}</tfoot>
  );
}

export namespace Row {
  export const Component = styled.tr<{ readonly hover?: boolean }>`
    border: none;

    ${() => Animation.Transition.styles(Animation.medium())}

    &:hover {
      ${({ hover = true }) =>
        hover ? Shadow.Box.styles(Shadow.medium()) : ""};

      background: ${({ hover = true }) =>
        hover ? Background.color() : "none"};
    }

    &:last-of-type {
      td {
        border-bottom: none;
      }
    }
  `;
}

export namespace Column {
  export const Component = styled.td`
    border: 1px solid ${() => veryWhiteGrey()};

    /* TODO: Resolve conflict with legacy CSS */
    white-space: normal;
    font-size: ${() => Typography.FontSize.data()};
    line-height: 20px;
  `;

  export namespace Detail {
    export const Component: Component = ({ children }) => (
      <Container.Component>
        <Typography.Component small>
          <Layout.Inline.Component top>{children}</Layout.Inline.Component>
        </Typography.Component>
      </Container.Component>
    );

    namespace Container {
      export const Component = styled.div`
        display: table-cell;
      `;
    }
  }
}

export namespace Wide {
  export type Wide = { readonly colSpan?: number };
  type Props = Wide & { readonly children?: Component.Children };

  export const Component: Component<Props> = ({ colSpan, children }) => (
    <Container.Component>
      <Row.Component hover={false}>
        <Column.Component colSpan={colSpan ?? 1}>{children}</Column.Component>
      </Row.Component>
    </Container.Component>
  );

  namespace Container {
    export const Component = styled.tbody`
      & > tr > td {
        width: 100%;
        padding: 2em;
        text-align: center;
      }
    `;
  }

  export namespace Jumbo {
    export const Component: Component<Props> = ({ children, ...props }) => (
      <Wide.Component {...props}>
        <Container.Component>
          <Typography.Component jumbo align="center">
            <Layout.Block.Component all>{children}</Layout.Block.Component>
          </Typography.Component>
        </Container.Component>
      </Wide.Component>
    );

    namespace Container {
      export const Component = styled.div`
        display: flex;
        justify-content: center;
      `;
    }
  }

  export namespace Loading {
    export const Component: Component<Wide> = (props) => (
      <Jumbo.Component {...props}>
        <LoadingComponent />
      </Jumbo.Component>
    );
  }

  export namespace ErrorMessage {
    export const Component: Component<Props> = ({ children, ...props }) => (
      <Jumbo.Component {...props}>
        <ErrorMessageComponent icon danger>
          {children}
        </ErrorMessageComponent>
      </Jumbo.Component>
    );
  }

  export namespace Empty {
    export const Component: Component<Wide> = (props) => (
      <Jumbo.Component {...props}>
        <Binoculars faded />
        <Layout.Inline.Component left>
          Nothing to see here...
        </Layout.Inline.Component>
      </Jumbo.Component>
    );
  }

  export namespace Title {
    export const Component: Component = ({ children }) => (
      <Typography.H1.Component>{children}</Typography.H1.Component>
    );
  }

  export namespace Image {
    export const Component = styled.img`
      display: inline-block;
      padding: 2em 0;
      max-height: 23em;
    `;
  }

  export namespace Caption {
    export const Component: Component = ({ children }) => (
      <Typography.H2.Component all>{children}</Typography.H2.Component>
    );
  }
}

namespace Background {
  export const color = (): Color => almostWhite();
}
